import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { PageHeading } from 'web-ui-components/lib/typography/headings';
import AccountTypeCard from '../../components/AccountTypeCard';
import * as actions from '../../redux/actions';
import MultipleAlert from '../../components/MultipleAlert';
import { getAlertAccountData, getProjectionData } from '../../businessLogic/AlertsBL';
import {
  UPPER_THRESHOLD,
  LOWER_THRESHOLD,
  PROJECTION,
  ALERT,
} from '../../constants/StringConstants';
import { CommonShell } from '../../components/common/CommonShell/CommonShell';
import { isLoading, getBrandId } from '../../infrastructure/config/selectors';
import AlertTypeWBAComponent from '../AlertTypeWba';
import ProjectionAlert from '../../components/ProjectionAlert';
import { getAccountDetails } from '../../businessLogic/AccountsBL';
import { CommonModal } from '../../components/common/CommonModal';

export const getValidationKey = (alertID) => {
  switch (alertID) {
    case UPPER_THRESHOLD:
      return 'highAlertValidation';
    case LOWER_THRESHOLD:
      return 'lowAlertValidation';
    case PROJECTION:
      return 'projectionValidation';
  }
};
export class BalanceAlerts extends Component {
  constructor(props) {
    super(props);
    this.handleBalanceRequestPacket = this.handleBalanceRequestPacket.bind(this);
    this.backClick = this.backClick.bind(this);
    this.errorModal = this.errorModal.bind(this);
    this.closeErrorModal = this.closeErrorModal.bind(this);
    this.setLowAlertToggleContent = this.setLowAlertToggleContent.bind(this);
    this.setHighAlertToggleContent = this.setHighAlertToggleContent.bind(this);
    this.state = {
      currentAlertId: null,
    };
  }
  componentDidUpdate(prevProps) {
    const { isEditModeEnabled, masterCardLeavePopup } = this.props;
    if (prevProps.isEditModeEnabled !== isEditModeEnabled && masterCardLeavePopup) {
      masterCardLeavePopup(isEditModeEnabled);
    }
  }
  componentWillUnmount() {
    const { masterCardLeavePopup, editModeEnabled } = this.props;
    if (masterCardLeavePopup) {
      masterCardLeavePopup(false);
    }
    editModeEnabled(false);
  }
  handleBalanceRequestPacket(highBalRequestPacket, alertID) {
    const {
      getBalanceAlert,
      accountDetails,
      getBulkBalanceAlerts,
      getBulkProjectionAlert,
      getProjectionAlertBalance,
      showAlertsError,
    } = this.props;
    if (alertID === PROJECTION) {
      getProjectionAlertBalance(highBalRequestPacket, accountDetails.id).then(() => {
        getBulkProjectionAlert();
      });
    } else {
      getBalanceAlert(highBalRequestPacket, accountDetails.id).then(() => {
        getBulkBalanceAlerts();
      });
    }
    showAlertsError(false, getValidationKey(alertID));
  }
  backClick() {
    const { isEditModeEnabled, routeTo, showUnsavedLeavePopup } = this.props;
    if (isEditModeEnabled) {
      showUnsavedLeavePopup(true);
    } else {
      routeTo('/singlealerts');
    }
  }
  closeErrorModal() {
    this.props.closeErrorPopup(false);
  }
  errorModal(type) {
    const { updateProjectionError, bulkBalanceError, alertRequestPacket, projectionRequestPacket } =
      this.props;
    switch (type) {
      case PROJECTION:
        return (
          <CommonModal
            isOpen={updateProjectionError.projectionError}
            onRequestClose={this.closeErrorModal}
            onPrimaryBtnClick={() =>
              this.handleBalanceRequestPacket(projectionRequestPacket, PROJECTION)
            }
          />
        );
      case ALERT:
        return (
          <CommonModal
            isOpen={bulkBalanceError}
            onRequestClose={this.closeErrorModal}
            onPrimaryBtnClick={() => this.handleBalanceRequestPacket(alertRequestPacket, ALERT)}
          />
        );
    }
  }
  setLowAlertToggleContent() {
    const { lowBalance, content } = this.props;
    return lowBalance.enabled ? content.lowAlertToggleText : content.lowAlertInfoText;
  }
  setHighAlertToggleContent() {
    const { highBalance, content } = this.props;
    return highBalance.enabled ? content.highAlertToggleText : content.highAlertInfoText;
  }
  render() {
    const {
      accountDetails,
      isLoading,
      content,
      highBalance,
      lowBalance,
      projectionBalance,
      brandID,
      updateProjectionError,
      bulkBalanceError,
      showAlertsError,
      highAlertValidation,
      lowAlertValidation,
      projectionValidation,
      editModeEnabled,
      isLeavePopupEnable,
      showUnsavedLeavePopup,
      routeTo,
    } = this.props;
    const { currentAlertId } = this.state;
    const isHighLoading = currentAlertId === UPPER_THRESHOLD && isLoading;
    const isLowLoading = currentAlertId === LOWER_THRESHOLD && isLoading;

    // SUVM-811: Disabling for VM at the moment
    const shouldShowProjectionAlert = projectionBalance && ['B'].includes(brandID);

    return (
      <CommonShell backClick={this.backClick}>
        <PageHeading>Balance alerts</PageHeading>
        <AccountTypeCard accountDetails={accountDetails} />
        <MultipleAlert
          title={content.lowAlertTitle}
          alertID={LOWER_THRESHOLD}
          alertPacket={lowBalance}
          isAlertActive={lowBalance.enabled}
          handleBalanceRequestPacket={this.handleBalanceRequestPacket}
          isLoading={isLowLoading}
          alertInfoText={this.setLowAlertToggleContent()}
          content={content}
          showAlertsError={(errorState, alertID) => {
            showAlertsError(errorState, getValidationKey(alertID));
          }}
          alertValidationMsg={lowAlertValidation}
          editModeEnabled={(editModeState) => {
            editModeEnabled(editModeState);
          }}
        />
        <AlertTypeWBAComponent content={content} />
        <MultipleAlert
          title={content.highAlertTitle}
          alertID={UPPER_THRESHOLD}
          alertPacket={highBalance}
          isAlertActive={highBalance.enabled}
          handleBalanceRequestPacket={this.handleBalanceRequestPacket}
          isLoading={isHighLoading}
          alertInfoText={this.setHighAlertToggleContent()}
          content={content}
          showAlertsError={(errorState, alertID) => {
            showAlertsError(errorState, getValidationKey(alertID));
          }}
          alertValidationMsg={highAlertValidation}
          editModeEnabled={(editModeState) => {
            editModeEnabled(editModeState);
          }}
        />

        {shouldShowProjectionAlert && (
          <ProjectionAlert
            title={content.projectionTitle}
            alertID={PROJECTION}
            alertPacket={projectionBalance}
            isAlertActive={projectionBalance.external_notifications[0].external_notification}
            handleBalanceRequestPacket={this.handleBalanceRequestPacket}
            isLoading={isLoading}
            content={content}
            showAlertsError={(errorState, alertID) => {
              showAlertsError(errorState, getValidationKey(alertID));
            }}
            alertValidationMsg={projectionValidation}
            editModeEnabled={(editModeState) => {
              editModeEnabled(editModeState);
            }}
          />
        )}
        {updateProjectionError && this.errorModal(PROJECTION)}
        {bulkBalanceError && this.errorModal(ALERT)}
        {isLeavePopupEnable && (
          <CommonModal
            isOpen={isLeavePopupEnable}
            onRequestClose={() => {
              showUnsavedLeavePopup(false);
              routeTo('/singlealerts');
            }}
            onPrimaryBtnClick={() => {
              showUnsavedLeavePopup(false);
            }}
            header={content.leavePopupHeader}
            infoCardContent={content.leavePopupContent}
            primaryBtnName={content.leavePopupPrimaryBtnName}
            secondaryBtnName={content.leavePopupSecondaryBtnName}
          />
        )}
      </CommonShell>
    );
  }
}

BalanceAlerts.propTypes = {
  accountDetails: PropTypes.shape({}).isRequired,
  isLoading: PropTypes.bool.isRequired,
  content: PropTypes.shape({}),
  highBalance: PropTypes.shape({}),
  lowBalance: PropTypes.shape({}),
  projectionBalance: PropTypes.shape({}),
  getBalanceAlert: PropTypes.func,
  getBulkBalanceAlerts: PropTypes.func,
  getBulkProjectionAlert: PropTypes.func,
  getProjectionAlertBalance: PropTypes.func,
  backClick: PropTypes.func,
  brandID: PropTypes.string,
  routeTo: PropTypes.func,
  closeErrorPopup: PropTypes.func,
  updateProjectionError: PropTypes.bool,
  bulkBalanceError: PropTypes.bool,
  showAlertsError: PropTypes.func,
  highAlertValidation: PropTypes.bool,
  lowAlertValidation: PropTypes.bool,
  projectionValidation: PropTypes.bool,
  alertRequestPacket: PropTypes.shape({}),
  projectionRequestPacket: PropTypes.shape({}),
  masterCardLeavePopup: PropTypes.func,
  isEditModeEnabled: PropTypes.bool,
  editModeEnabled: PropTypes.func,
  isLeavePopupEnable: PropTypes.bool,
  showUnsavedLeavePopup: PropTypes.func,
};

const mapStateToProps = ({
  accountList,
  bulkProjectionAlert,
  loader,
  bulkBalanceAlerts,
  config,
  validation,
}) => {
  return {
    accountDetails: getAccountDetails(
      accountList.get('selectedAccountId'),
      accountList.get('accountList').accounts
    ),
    isLoading: isLoading(loader),
    brandID: getBrandId(config),
    projectionBalance: getProjectionData(bulkProjectionAlert),
    highBalance: getAlertAccountData(
      bulkBalanceAlerts,
      accountList.get('selectedAccountId'),
      UPPER_THRESHOLD
    ),
    lowBalance: getAlertAccountData(
      bulkBalanceAlerts,
      accountList.get('selectedAccountId'),
      LOWER_THRESHOLD
    ),
    updateProjectionError: bulkProjectionAlert.get('projectionError'),
    bulkBalanceError: bulkBalanceAlerts.get('balanceError'),
    highAlertValidation: validation.get('highAlertValidation'),
    lowAlertValidation: validation.get('lowAlertValidation'),
    projectionValidation: validation.get('projectionValidation'),
    projectionRequestPacket: bulkProjectionAlert.get('projectionRequestPacket'),
    alertRequestPacket: bulkBalanceAlerts.get('alertRequestPacket'),
    isEditModeEnabled: validation.get('enableEditMode'),
    isLeavePopupEnable: validation.get('enableUnsavedLeavePopup'),
  };
};
const mapDispatch = (dispatch) => bindActionCreators(actions, dispatch);

export default connect(mapStateToProps, mapDispatch)(BalanceAlerts);
